'use client'

import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCcw, Shapes } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import type { ChartConfig } from '@ui/web/components'
import { Button, ChartContainer, ChartTooltip, ChartTooltipContent, NumberFlow, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../context'

// Reading = chart-2 across the dashboard (see reading-trend/charts.tsx).
const ACTIVITY_CONFIG = {
  minutes: { label: 'Menit baca', color: 'var(--chart-2)' },
} satisfies ChartConfig

interface DailyMetric {
  date: Date | string
  totalReadingSeconds: number
  sessionCount: number
  uniqueBooksRead: number
  completedBooksCount: number
  themeDistribution: unknown
}

/** Aggregate theme distribution (categoryId -> % of that day's reading) weighted by reading seconds. */
function aggregateThemeShare(daily: DailyMetric[], idToName: Record<string, string>): { name: string; pct: number }[] {
  const totalSeconds = daily.reduce((acc, d) => acc + d.totalReadingSeconds, 0)
  if (totalSeconds === 0) return []

  const weighted = new Map<string, number>()
  for (const d of daily) {
    const raw = d.themeDistribution
    const dist = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, number | string>) : null
    if (!dist) continue
    for (const [id, pct] of Object.entries(dist)) {
      const sec = (d.totalReadingSeconds * (typeof pct === 'number' ? pct : 0)) / 100
      weighted.set(id, (weighted.get(id) ?? 0) + sec)
    }
  }
  return Array.from(weighted.entries())
    .map(([id, sec]) => ({ name: idToName[id] ?? id, pct: (sec / totalSeconds) * 100 }))
    .filter((v) => v.pct > 0)
    .sort((a, b) => b.pct - a.pct)
}

function formatDayLabel(value: Date | string): string {
  return new Date(value).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })
}

function toUtcDayKey(date: Date): string {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * One entry per UTC calendar day in [from, to], filling days without reading with zeros.
 * Mirrors fillDailyRange in lesson-overview.tsx. Bucketed in UTC to match
 * ReadingMetricsDaily.date (stored at UTC start-of-day) and the UTC window getReadingMetrics
 * applies, so every row the API returns lands on a bar — no edge day gets clipped.
 * The API only zero-fills gaps for children with no data at all; a child with sparse activity
 * gets one row per active day, so without this the period would collapse (e.g. "1 hari dari
 * 1 hari" for a 90-day filter) and the activity chart would render a discontinuous timeline.
 */
function fillReadingRange(daily: DailyMetric[], from: Date, to: Date): DailyMetric[] {
  const byDay = new Map<string, DailyMetric>()
  for (const d of daily) {
    byDay.set(toUtcDayKey(new Date(d.date)), d)
  }

  const cursor = new Date(from)
  cursor.setUTCHours(0, 0, 0, 0)
  const end = new Date(to)
  end.setUTCHours(0, 0, 0, 0)

  const result: DailyMetric[] = []
  while (cursor.getTime() <= end.getTime()) {
    const existing = byDay.get(toUtcDayKey(cursor))
    result.push(
      existing ?? {
        date: new Date(cursor),
        totalReadingSeconds: 0,
        sessionCount: 0,
        uniqueBooksRead: 0,
        completedBooksCount: 0,
        themeDistribution: null,
      },
    )
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }

  return result
}

// ---------------------------------------------------------------------------
// Building blocks — card styling mirrors ui/shared/src/lib/dashboard-tabs.
// ---------------------------------------------------------------------------

const chartCardClassName =
  'srd:flex srd:flex-col srd:gap-2 srd:rounded-lg srd:border srd:border-grey-30 srd:bg-neutral-white srd:p-3'

function StatCard({
  label,
  value,
  suffix,
  caption,
  maxFraction = 0,
}: {
  label: string
  value: number
  suffix: string
  caption: string
  maxFraction?: number
}) {
  return (
    <section className='srd:flex srd:min-w-0 srd:flex-col srd:gap-2 srd:rounded-2xl srd:border srd:border-grey-30 srd:bg-neutral-white srd:p-4 srd:text-neutral-black'>
      <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-grey-100')}>{label}</p>
      <NumberFlow
        value={value}
        suffix={suffix}
        format={{ minimumFractionDigits: 0, maximumFractionDigits: maxFraction }}
        className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:font-bold')}
      />
      <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>{caption}</p>
    </section>
  )
}

function ActivityPanel({ data, avgMinutes }: { data: { date: Date | string; minutes: number }[]; avgMinutes: number }) {
  return (
    <section className={cn(chartCardClassName, 'srd:lg:col-span-2')}>
      <p className={cn(typographyVariants({ variant: 'title-md' }))}>Aktivitas membaca</p>

      <div className='srd:mt-2 srd:flex srd:flex-1 srd:flex-col srd:gap-2'>
        <ChartContainer
          config={ACTIVITY_CONFIG}
          className={cn('srd:min-h-[220px] srd:h-auto srd:aspect-auto srd:w-full')}
        >
          <BarChart accessibilityLayer data={data} margin={{ left: 0, right: 8, top: 16, bottom: 8 }}>
            <CartesianGrid vertical={false} strokeDasharray='4 4' />
            <XAxis
              dataKey='date'
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(v) => formatDayLabel(v as string)}
              minTickGap={24}
            />
            <YAxis tickLine={false} axisLine={false} width={32} domain={[0, 'auto']} allowDecimals={false} />
            <ChartTooltip content={<ChartTooltipContent labelFormatter={(v) => formatDayLabel(String(v))} />} />
            <Bar dataKey='minutes' fill='var(--color-minutes)' radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartContainer>

        <div className='srd:mt-auto srd:flex srd:flex-col srd:gap-y-1 srd:border-t srd:border-outline-variant/50 srd:pt-2'>
          <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>Rata-rata per hari</p>
          <NumberFlow
            value={avgMinutes}
            suffix=' menit'
            format={{ minimumFractionDigits: 0, maximumFractionDigits: 1 }}
            className={cn(typographyVariants({ variant: 'body-md' }), 'srd:font-bold')}
          />
        </div>
      </div>
    </section>
  )
}

function CompletionRow({
  icon,
  title,
  caption,
  value,
  emphasized,
}: {
  icon?: ReactNode
  title: string
  caption?: string
  value: number
  emphasized?: boolean
}) {
  return (
    <div className='srd:flex srd:items-center srd:gap-3'>
      {icon}
      <div className='srd:flex srd:min-w-0 srd:flex-col'>
        <p className={cn(typographyVariants({ variant: emphasized ? 'title-sm' : 'body-md' }), 'srd:font-semibold')}>
          {title}
        </p>
        {caption && <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>{caption}</p>}
      </div>
      <NumberFlow
        value={value}
        suffix='%'
        format={{ minimumFractionDigits: 0, maximumFractionDigits: 0 }}
        className={cn(typographyVariants({ variant: emphasized ? 'body-lg' : 'body-md' }), 'srd:ml-auto srd:font-bold')}
      />
    </div>
  )
}

function CompletionPanel({
  completionPct,
  booksCompleted,
  booksOpened,
  genres,
}: {
  completionPct: number
  booksCompleted: number
  booksOpened: number
  genres: { name: string; pct: number }[]
}) {
  return (
    <section className={chartCardClassName}>
      <p className={cn(typographyVariants({ variant: 'title-md' }))}>Selesai Baca</p>

      <CompletionRow
        title='Total'
        caption={`${booksCompleted} dari ${booksOpened} buku`}
        value={completionPct}
        emphasized
      />

      {genres.length > 0 && (
        <div className='srd:flex srd:flex-col srd:gap-3 srd:border-t srd:border-outline-variant/50 srd:pt-3'>
          <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>Genre yang dibaca</p>
          {genres.map((genre) => (
            <CompletionRow
              key={genre.name}
              icon={
                <span className='srd:flex srd:size-10 srd:shrink-0 srd:items-center srd:justify-center srd:rounded-lg srd:bg-grey-10 srd:text-grey-100'>
                  <Shapes className='srd:size-5' />
                </span>
              }
              title={genre.name}
              value={genre.pct}
            />
          ))}
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Public component — real data from metrics.getReadingMetrics, driven by [from, to].
// ---------------------------------------------------------------------------

interface ReadingOverviewProps {
  childId: string
  from: Date
  to: Date
  className?: string
}

export function ReadingOverview({ childId, from, to, className }: ReadingOverviewProps) {
  const trpc = useSharedTRPC()

  const { data, isPending, isError, refetch } = useQuery(
    trpc.metrics.getReadingMetrics.queryOptions({ userId: [childId], from, to }),
  )

  if (isError) {
    return (
      <section
        className={cn(
          'srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-2 srd:rounded-lg srd:border srd:border-grey-30 srd:bg-neutral-white srd:py-10',
          className,
        )}
      >
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-main-red-100')}>
          Gagal memuat metrik baca
        </p>
        <Button variant='outline' size='sm' onClick={() => void refetch()}>
          <RefreshCcw className='srd:size-4 srd:mr-2 srd:text-main-blue-100' />
          Coba lagi
        </Button>
      </section>
    )
  }

  if (isPending) {
    return (
      <div className={cn('srd:flex srd:w-full srd:flex-col srd:gap-4', className)}>
        <div className='srd:grid srd:grid-cols-1 srd:gap-4 srd:sm:grid-cols-3'>
          <Skeleton className='srd:h-28 srd:w-full srd:rounded-2xl' />
          <Skeleton className='srd:h-28 srd:w-full srd:rounded-2xl' />
          <Skeleton className='srd:h-28 srd:w-full srd:rounded-2xl' />
        </div>
        <div className='srd:grid srd:grid-cols-1 srd:gap-3 srd:lg:grid-cols-3'>
          <Skeleton className='srd:h-[320px] srd:w-full srd:rounded-lg srd:lg:col-span-2' />
          <Skeleton className='srd:h-[320px] srd:w-full srd:rounded-lg' />
        </div>
      </div>
    )
  }

  const row = data.metrics.find((m) => m.id === childId)
  // Fill every calendar day in the selected [from, to] range so metrics reflect the
  // period, not just the days the API returned rows for (see fillReadingRange).
  const daily = fillReadingRange(row?.dailyMetrics ?? [], from, to)

  const periodDays = daily.length
  const activeDays = daily.filter((d) => d.totalReadingSeconds > 0).length
  const totalSeconds = daily.reduce((s, d) => s + d.totalReadingSeconds, 0)
  const totalSessions = daily.reduce((s, d) => s + d.sessionCount, 0)
  // Distinct books over the whole window (from the API), NOT a sum of per-day uniqueBooksRead —
  // summing double-counts a book read across multiple days. These come from the same table as the
  // reading history list (ReadingHistoryDaily), so the "X dari Y buku" card agrees with that list.
  const booksOpened = row?.distinctBooksOpened ?? 0
  const booksCompleted = row?.distinctBooksCompleted ?? 0

  const consistencyPct = periodDays > 0 ? (activeDays / periodDays) * 100 : 0
  const staminaMinutes = totalSessions > 0 ? totalSeconds / 60 / totalSessions : 0
  const completionPct = booksOpened > 0 ? (booksCompleted / booksOpened) * 100 : 0
  const avgMinutesPerDay = periodDays > 0 ? totalSeconds / 60 / periodDays : 0

  // ISO string (not a Date object) so the tooltip label resolves correctly — mirrors dashboard charts.
  const chartData = daily.map((d) => ({
    date: new Date(d.date).toISOString(),
    minutes: Math.round(d.totalReadingSeconds / 60),
  }))
  const genres = aggregateThemeShare(daily, data.lookups.theme).slice(0, 3)

  return (
    <div className={cn('srd:flex srd:w-full srd:flex-col srd:gap-4', className)}>
      <div className='srd:grid srd:grid-cols-1 srd:gap-4 srd:sm:grid-cols-3'>
        <StatCard
          label='Konsistensi Baca'
          value={consistencyPct}
          suffix='%'
          caption={`${activeDays} hari dari ${periodDays} hari`}
        />
        <StatCard label='Stamina Baca' value={staminaMinutes} suffix=' menit' caption='per sesi' maxFraction={1} />
        <StatCard label='Frekuensi Baca' value={totalSessions} suffix=' sesi' caption={`${booksOpened} buku dibuka`} />
      </div>

      <div className='srd:grid srd:grid-cols-1 srd:items-stretch srd:gap-3 srd:lg:grid-cols-3'>
        <ActivityPanel data={chartData} avgMinutes={avgMinutesPerDay} />
        <CompletionPanel
          completionPct={completionPct}
          booksCompleted={booksCompleted}
          booksOpened={booksOpened}
          genres={genres}
        />
      </div>
    </div>
  )
}
